import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import AdminList from "@/components/AdminList"
import { useGetAdminList } from "@/hooks/api/use-get-admin-list"
import useCurrentUser from "@/hooks/use-current-user"
import { councilAdminApi } from "@/lib/api"
import { AdminDTO } from "@/gen/api"
import { hasPermission, hasReadPermission } from "@/lib/admin-permissions"
import { Plus, Users } from "lucide-react"

const adminName = (admin?: AdminDTO | null) =>
  admin?.name ||
  [admin?.firstName, admin?.lastName].filter(Boolean).join(" ") ||
  admin?.email ||
  "Admin"

type ManageAdminPageProps = {
  mode?: "n3" | "council-admins"
}

export default function ManageAdminPage({ mode = "n3" }: ManageAdminPageProps) {
  const { councilId } = useParams()
  const navigate = useNavigate()
  const scopedCouncilId = councilId ? Number(councilId) : undefined
  const isCouncilAdminsSurface = mode === "council-admins"
  const { user } = useAuth()
  const { toast } = useToast()
  const { hasRole } = useCurrentUser()
  const canReadAdmin =
    hasRole("super_admin") ||
    hasReadPermission(user?.permissions, "admin_management", scopedCouncilId)
  const { data: admins = [], isLoading, error } = useGetAdminList(!scopedCouncilId && canReadAdmin)
  const [scopedAdmins, setScopedAdmins] = useState<AdminDTO[]>([])
  const [isScopedLoading, setIsScopedLoading] = useState(false)
  const [scopedError, setScopedError] = useState<Error | null>(null)

  const loadScopedAdmins = useCallback(async () => {
    if (!scopedCouncilId) return
    setIsScopedLoading(true)
    setScopedError(null)
    try {
      const response = await councilAdminApi.getCouncilAdmins(scopedCouncilId)
      setScopedAdmins(response.data.data ?? [])
    } catch (error) {
      setScopedError(error as Error)
    } finally {
      setIsScopedLoading(false)
    }
  }, [scopedCouncilId])

  useEffect(() => {
    if (scopedCouncilId && canReadAdmin) {
      void loadScopedAdmins()
    }
  }, [scopedCouncilId, canReadAdmin, loadScopedAdmins])

  const detailsBasePath = scopedCouncilId
    ? `/council/${scopedCouncilId}/manage-admin`
    : isCouncilAdminsSurface
      ? "/manage-admin/council-admins"
      : "/manage-admin"
  const createPath = `${detailsBasePath}/create`
  const canCreateAdmin =
    !isCouncilAdminsSurface &&
    (hasRole("super_admin") ||
      hasPermission(user?.permissions, "admin_management", "write_all", scopedCouncilId))

  const openDetails = (admin: AdminDTO) => {
    if (!admin.id) return
    navigate(`${detailsBasePath}/${admin.id}`)
  }

  const filteredAdmins = hasRole("super_admin")
    ? scopedCouncilId
      ? scopedAdmins
      : isCouncilAdminsSurface
        ? admins.filter((admin) => admin.role === "council_admin")
        : admins.filter((admin) => admin.role === "super_admin" || admin.role === "admin")
    : admins.filter((admin) => admin.councilId === user?.councilId)

  const handleDeleteAdmin = async (admin: AdminDTO) => {
    if (scopedCouncilId && admin.id != null) {
      await councilAdminApi.deleteCouncilAdmin(scopedCouncilId, Number(admin.id))
      await loadScopedAdmins()
      toast({
        title: "Admin Deleted",
        description: `${adminName(admin)} has been removed from this council.`,
      })
    }
  }

  if (!canReadAdmin) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-xl font-semibold">Access denied</h2>
        <p className="mt-2 text-muted-foreground">
          You need Admin Management read permission to view admins.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between pb-6">
        <div>
          <h1 className="text-3xl font-bold">
            {isCouncilAdminsSurface ? "Council Admins" : "Manage Admins"}
          </h1>
          <p className="text-muted-foreground">
            {isCouncilAdminsSurface
              ? "Search and manage council admin access across Water Bureaus"
              : hasRole("super_admin")
                ? scopedCouncilId
                  ? "Create and manage admins for this council"
                  : "Create and manage Nucleus3 admins"
                : "Manage admins for your council"}
          </p>
        </div>
        <div className="flex gap-2">
          {!scopedCouncilId && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(isCouncilAdminsSurface ? "/manage-admin" : "/manage-admin/council-admins")
              }
            >
              <Users className="mr-2 size-4" />
              {isCouncilAdminsSurface ? "N3 Admins" : "Council Admins"}
            </Button>
          )}
          {!isCouncilAdminsSurface && (
            <Button onClick={() => navigate(createPath)} disabled={!canCreateAdmin}>
              <Plus className="mr-2 size-4" />
              Add Admin
            </Button>
          )}
        </div>
      </div>

      <AdminList
        className="min-h-0 flex-1"
        admins={filteredAdmins}
        isLoading={scopedCouncilId ? isScopedLoading : isLoading}
        error={scopedCouncilId ? scopedError : error}
        onEdit={openDetails}
        onDelete={scopedCouncilId ? handleDeleteAdmin : undefined}
      />
    </div>
  )
}
